// ルート描画用のマップキャンバス。
// fork_webapp_react の MapCanvas から、シナリオ編集プレビューに必要な範囲を移植。
// 状態は jotai ではなく props で受け取り、ロボット現在位置・センサ描画は含まない。

import { useMemo, useEffect, useState, useRef } from 'react'
import { BoxSelect, Pause, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Button from '@/components/common/Button'
import { useCanvasDimensions } from '@/hooks/useCanvasDimensions'
import type { RouteMap, MapCanvasParams, RouteWithAction, Pose } from '@/types'
import {
  calculateRoutesBounds,
  calculateImageBounds,
  fitWidthMeterForBounds,
  boundsKey,
  buildRoutePoints,
  type BoundingBox,
} from '@/common/mapUtil'
import {
  drawRouteMapCanvas,
  drawEditMarks,
  editRotateHandleGeometry,
  EDIT_MARK_HIT_RADIUS,
  type EditMarkDisplay,
} from '@/common/drawUtil'
import {
  useMapCanvas,
  createWorldToCanvas,
  drawBackgroundImage,
  drawForklift,
} from '@/hooks/useMapCanvas'
import { MapCanvasControls } from './MapCanvasControl'
import { ENABLE_CARGO_TOGGLE, SLOT_TYPE_CARGO } from '@/config/scenarioConfig'

// マップ上で選択可能な点（スロット / ルートの中継点）
export interface SelectedPoint {
  id: string
  kind: 'slot' | 'point'
  x: number
  y: number
  theta: number
  /** 高さ保存スロットの場合の高さ (m) */
  height?: number
  /** 高さ保存（cargo）スロットか */
  isCargo?: boolean
}

// バウンディングボックスを値ベースで安定化する。範囲の値（boundsKey）が
// 変わらない限り前回と同じオブジェクトを返し、useEffect の依存として使っても
// 無用な再実行（autoFit のリセット等）が起きないようにする。
function useStableBounds(bounds: BoundingBox | null): BoundingBox | null {
  const ref = useRef<{ key: string; value: BoundingBox | null }>({
    key: boundsKey(bounds),
    value: bounds,
  })
  const key = boundsKey(bounds)
  if (key !== ref.current.key) {
    ref.current = { key, value: bounds }
  }
  return ref.current.value
}

export interface MapCanvasProps {
  map: RouteMap | null
  canvasParams: MapCanvasParams
  bgImage: HTMLImageElement | null
  imageLoaded: boolean
  routes: RouteWithAction[]
  /**
   * 縮尺・中心の基準にするルート群（autoFit / センタリング用）。
   * 未指定なら routes を使う。描画対象が変わっても縮尺を固定したい場合
   * （編集中も一覧と同じ縮尺を保つ等）に、常に同じ集合を渡す。
   */
  fitRoutes?: RouteWithAction[]
  /**
   * 縮尺・中心の基準にするマップ（autoFit 用）。未指定なら map を使う。
   * 編集中のマップ（map）を描画しつつ、編集前のマップを渡すことで
   * スロット追加・移動でフィット対象が変わらず、ズームが維持される。
   */
  fitMap?: RouteMap | null
  autoFit?: boolean
  preview?: boolean
  showControls?: boolean
  showSlots?: boolean
  showSlotLabels?: boolean
  /** 最後のルートをハイライトするか（プレビュールート用） */
  highlightLastRoute?: boolean
  /** 選択されたルートのインデックス（エディタの単一選択用） */
  selectedRouteIndex?: number | null
  /** ハイライトするルートインデックスの集合（一覧の複数ハイライト用） */
  highlightedIndices?: Set<number>
  /** 青でハイライトするルートインデックスの集合（使用ルート一覧からの選択用） */
  blueHighlightedIndices?: Set<number>
  /** アニメ再生開始時にこのシナリオインデックスのルートを先に1回再生してから先頭ループに入る */
  forkAnimFirstRouteIndex?: number | null
  /**
   * 爪方向アニメーションの走行ルート列（配列順＝シナリオ順に走行）。
   * 描画対象（routes）と別の順序で再生したい場合に指定する。未指定なら routes を使う。
   */
  forkAnimRoutes?: RouteWithAction[]
  /**
   * 爪方向アニメーションの初期状態。false なら停止状態で始まり、
   * 再生ボタンで開始する。モード切替や forkAnimRoutes の差し替え時にこの値へ戻る。
   */
  forkAnimDefaultEnabled?: boolean
  onMapClick?: (worldPos: { x: number; y: number }) => void
  /** 選択中の点の ID 集合（ハイライト表示用） */
  selectedPointIds?: Set<string> | null
  /**
   * 点（スロット / 中継点）を選択・選択解除したときのコールバック。
   * 修飾キー付きクリック（複数選択）は onSelectSlots が有効なときのみ働く。
   */
  onSelectPoint?: (
    point: SelectedPoint | null,
    mods: { ctrl: boolean; shift: boolean }
  ) => void
  /** ルートをクリック選択したときのコールバック（修飾キーなしのみ。null=空クリック） */
  onSelectRoute?: (
    route: RouteWithAction | null,
    mods: { ctrl: boolean; shift: boolean }
  ) => void
  /**
   * 矩形選択で複数の点（スロット / 中継点）を選択したときのコールバック
   * （修飾キー付き）。未指定なら複数選択（矩形・修飾キー）自体が無効になる。
   */
  onSelectSlots?: (
    slots: SelectedPoint[],
    mods: { ctrl: boolean; shift: boolean }
  ) => void
  /** 選択中の点のうちドラッグで平行移動できるものの ID 集合 */
  movablePointIds?: Set<string>
  /**
   * 参照モード: 回転ハンドルを表示する選択中の点の ID（記録ルートの端点スロット）。
   * ハンドルのドラッグ中、onPointRotate へ新しい向き (rad) を通知する。
   */
  rotatablePointId?: string | null
  onPointRotate?: (theta: number) => void
  /** 回転ハンドルのドラッグ開始 / 終了（Undo 履歴のまとめ等に使う） */
  onPointRotateStart?: () => void
  onPointRotateEnd?: () => void
  /** 選択中の点の平行移動プレビュー用オフセット (m)。保存前の移動量 */
  selectionOffset?: { x: number; y: number } | null
  /** 選択中の点のドラッグ移動開始（移動量が閾値を超えて確定したとき） */
  onSelectionDragStart?: () => void
  /** 選択中の点のドラッグ移動中（ドラッグ開始位置からのワールド座標差分） */
  onSelectionDrag?: (delta: { x: number; y: number }) => void
  /** 選択中の点のドラッグ移動終了 */
  onSelectionDragEnd?: () => void
  /** 全ルートを淡色で描画する（ルート編集モードの背景表示用） */
  dimRoutes?: boolean
  /** ルート編集モード: 編集中のマーク列（非 null のとき編集モード描画・操作になる） */
  editMarks?: EditMarkDisplay[] | null
  /** ルート編集モード: 選択中のマークインデックス */
  selectedMarkIndex?: number | null
  /** ルート編集モード: エラーのあるマークインデックスの集合 */
  markErrorIndices?: Set<number>
  /** ルート編集モード: マーク追加モード（クリックで挿入） */
  isAddingMark?: boolean
  /** ルート編集モード: 回転ハンドルを表示するマーク（選択中の端点マーク） */
  rotatableMarkIndex?: number | null
  onSelectMark?: (index: number | null) => void
  onMarkDragStart?: (index: number) => void
  /**
   * マークのドラッグ移動。既存スロットへ吸着した場合は snapTheta に
   * そのスロットの向きを渡す（ドラッグ終了時の角度あわせ用）。
   */
  onMarkDrag?: (
    index: number,
    pos: { x: number; y: number },
    snapTheta?: number
  ) => void
  onMarkDragEnd?: () => void
  onMarkRotateStart?: (index: number) => void
  onMarkRotate?: (index: number, theta: number) => void
  onMarkRotateEnd?: () => void
  /**
   * マーク追加モードのクリック。既存スロットの近くをクリックした場合は
   * snap にそのスロットを渡す（座標吸着・荷役高さの引き継ぎ用）。
   */
  onAddMarkAt?: (pos: { x: number; y: number }, snap?: SelectedPoint) => void
  className?: string
  canvasClassName?: string
}

// 参照モードの選択点ハイライト（黄色いリング / 矢印）と回転ハンドルの寸法
const POINT_RING_R = 14
const POINT_ROTATE_HANDLE_DIST = POINT_RING_R + 34
const POINT_ROTATE_HANDLE_R = 9

// 点からの線分への最短距離の二乗（キャンバス座標）
function distToSegmentSq(
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lenSq = dx * dx + dy * dy
  let t = lenSq === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  const cx = a.x + t * dx
  const cy = a.y + t * dy
  return (p.x - cx) ** 2 + (p.y - cy) ** 2
}

export function MapCanvas({
  map,
  canvasParams,
  bgImage,
  imageLoaded,
  routes,
  fitRoutes,
  fitMap,
  autoFit = false,
  preview = false,
  showControls = true,
  showSlots = true,
  showSlotLabels = true,
  highlightLastRoute = false,
  selectedRouteIndex,
  highlightedIndices,
  blueHighlightedIndices,
  forkAnimFirstRouteIndex = null,
  forkAnimRoutes,
  forkAnimDefaultEnabled = true,
  onMapClick,
  selectedPointIds,
  onSelectPoint,
  onSelectRoute,
  onSelectSlots,
  movablePointIds,
  rotatablePointId = null,
  onPointRotate,
  onPointRotateStart,
  onPointRotateEnd,
  selectionOffset,
  onSelectionDragStart,
  onSelectionDrag,
  onSelectionDragEnd,
  dimRoutes = false,
  editMarks = null,
  selectedMarkIndex = null,
  markErrorIndices,
  isAddingMark = false,
  rotatableMarkIndex = null,
  onSelectMark,
  onMarkDragStart,
  onMarkDrag,
  onMarkDragEnd,
  onMarkRotateStart,
  onMarkRotate,
  onMarkRotateEnd,
  onAddMarkAt,
  className = 'relative w-full h-full select-none',
  canvasClassName = 'w-full h-full bg-gray-200 rounded',
}: MapCanvasProps) {
  const { t } = useTranslation()
  const { containerRef, dimensions: canvasSize } = useCanvasDimensions()
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null)

  // ルート編集モード（マークのドラッグ移動・クリック選択・追加・回転）
  const isEditingMarks = editMarks !== null
  // ドラッグ移動中のマークインデックス（ドラッグ中はパンを無効化する）
  const draggingMarkRef = useRef<number | null>(null)
  // マークドラッグの掴み情報。マーク中心をポインタへ移すのではなく、
  // 掴んだ位置とマーク中心のずれ（クライアント px）を保って動かす。
  // active は閾値超えでドラッグ確定後 true。未確定（クリックの手ぶれ）では
  // マークを動かさない。
  const markDragRef = useRef<{
    startClientX: number
    startClientY: number
    grabDx: number
    grabDy: number
    active: boolean
  } | null>(null)
  // 回転ハンドルをドラッグ中のマークインデックス
  const rotatingMarkRef = useRef<number | null>(null)
  // 選択スロットの平行移動ドラッグ状態。active はドラッグ確定
  // （閾値超え）後 true。未確定のまま離すと通常のクリックとして扱う。
  const selectionDragRef = useRef<{
    startClientX: number
    startClientY: number
    startWorld: { x: number; y: number }
    active: boolean
  } | null>(null)
  // 参照モードの選択点回転ハンドルをドラッグ中の回転中心（ワールド座標）
  const rotatingPointRef = useRef<{ x: number; y: number } | null>(null)
  // 回転ハンドル操作直後のクリックで選択が解除されるのを防ぐフラグ
  const rotatedPointerRef = useRef(false)

  // 矩形選択モード（ドラッグでスロットを複数選択）
  const [selectionMode, setSelectionMode] = useState(false)
  const panDisabledRef = useRef(false)
  panDisabledRef.current =
    selectionMode ||
    draggingMarkRef.current !== null ||
    rotatingMarkRef.current !== null ||
    rotatingPointRef.current !== null ||
    selectionDragRef.current !== null
  // 矩形選択中の状態（キャンバス相対 CSS 座標）
  const selectingRef = useRef(false)
  const rectStartRef = useRef<{ x: number; y: number } | null>(null)
  const [dragRect, setDragRect] = useState<{
    left: number
    top: number
    width: number
    height: number
  } | null>(null)

  // 爪方向アニメーション（ルートに沿ってフォークリフトを走行表示）の ON/OFF
  const [forkAnimEnabled, setForkAnimEnabled] = useState(forkAnimDefaultEnabled)
  // モード切替・アニメ対象ルート列の差し替え時は初期状態（参照モードは停止）に戻す
  useEffect(() => {
    setForkAnimEnabled(forkAnimDefaultEnabled)
  }, [forkAnimDefaultEnabled, forkAnimRoutes])
  // 爪方向アニメーション用タイマー（~30fpsで再描画トリガー）
  const [forkAnimTick, setForkAnimTick] = useState(0)
  // 走行させるルート列。forkAnimRoutes 指定時はその順（シナリオ順）で走行する
  const animSourceRoutes = forkAnimRoutes ?? routes
  // ルート編集中は背景ルートのアニメーションを止めて編集対象に集中させる
  const hasForkAnimation =
    forkAnimEnabled && animSourceRoutes.length > 0 && !isEditingMarks
  // ルート構成（選択・方向反転含む）が変わったら最初から再生し直すためのキー
  const forkAnimRoutesKey = animSourceRoutes
    .map((r) => `${r.id}:${r.startSlot ?? ''}:${r.endSlot ?? ''}`)
    .join('|')
  // 再生開始時刻。停止→再生やルート変更・選択変更のたびに更新し、
  // 選択ルートのイントロ再生（あれば）→先頭からのループ再生をやり直す
  const forkAnimStartRef = useRef(0)
  useEffect(() => {
    if (!hasForkAnimation) return
    forkAnimStartRef.current = Date.now()
    const id = setInterval(() => setForkAnimTick((t) => t + 1), 33)
    return () => clearInterval(id)
  }, [hasForkAnimation, forkAnimRoutesKey, forkAnimFirstRouteIndex])

  // 縮尺・中心は fitRoutes（未指定なら routes）を基準にする
  const baseRoutes = fitRoutes ?? routes

  // フィット対象のワールド座標バウンディングボックス。
  // 依存値のオブジェクトが差し替わっても範囲の値が同じなら同一の参照を返す
  // （useStableBounds）。これを怠ると、スロット編集で map が更新されるたびに
  // activeBounds が「変わった」と判定され、ズーム中にフィット表示へ戻ってしまう。
  const imageBounds = useStableBounds(
    useMemo(
      () => calculateImageBounds(imageLoaded ? bgImage : null, canvasParams),
      [bgImage, imageLoaded, canvasParams]
    )
  )
  const baseMap = fitMap === undefined ? map : fitMap
  const routesBounds = useStableBounds(
    useMemo(
      () => calculateRoutesBounds(baseMap, baseRoutes),
      [baseMap, baseRoutes]
    )
  )
  // 選択中（ハイライト）のルートのバウンディングボックス（選択パス全体表示用）
  const selectedRoutes = useMemo(() => {
    const idxs = new Set<number>()
    highlightedIndices?.forEach((i) => idxs.add(i))
    if (selectedRouteIndex != null) idxs.add(selectedRouteIndex)
    return [...idxs].map((i) => routes[i]).filter(Boolean)
  }, [routes, highlightedIndices, selectedRouteIndex])
  const selectedBounds = useMemo(
    () =>
      selectedRoutes.length ? calculateRoutesBounds(map, selectedRoutes) : null,
    [map, selectedRoutes]
  )

  // ボタンで指定したフィット対象のスナップショット。null のときは既定
  // （背景画像があれば画像全体、なければルート全体）にフィットする。
  const [fitOverride, setFitOverride] = useState<BoundingBox | null>(null)
  // データ（縮尺基準ルート / 背景画像）が変わったら上書きを解除して既定に戻す
  useEffect(() => {
    setFitOverride(null)
  }, [routesBounds, imageBounds])

  const activeBounds = fitOverride ?? imageBounds ?? routesBounds
  const isImageView = activeBounds !== null && activeBounds === imageBounds

  const effectiveCanvasWidthMeter = useMemo(() => {
    if (!autoFit || !activeBounds) return canvasParams.canvasWidthMeter
    return fitWidthMeterForBounds(
      activeBounds,
      canvasParams,
      canvasSize,
      isImageView ? 0.02 : 0.12
    )
  }, [autoFit, activeBounds, isImageView, canvasParams, canvasSize])

  const {
    canvasRef,
    containerRef: mapContainerRef,
    scale,
    offset,
    handleZoomIn,
    handleZoomOut,
    handleResetView,
  } = useMapCanvas(canvasParams, effectiveCanvasWidthMeter, panDisabledRef)

  const handleFitImage = () => {
    if (imageBounds) {
      setFitOverride(imageBounds)
      handleResetView()
    }
  }
  const handleFitSelected = () => {
    if (selectedBounds) {
      setFitOverride(selectedBounds)
      handleResetView()
    }
  }

  // ハイライト対象（単一選択 + 複数ハイライト集合 + 青ハイライト）を判定するヘルパー
  const isHighlightedIndex = useMemo(() => {
    return (idx: number) =>
      selectedRouteIndex === idx ||
      (highlightedIndices?.has(idx) ?? false) ||
      (blueHighlightedIndices?.has(idx) ?? false)
  }, [selectedRouteIndex, highlightedIndices, blueHighlightedIndices])

  // ハイライト対象を後に描画して前面に出す。青（使用ルート一覧で選択）は最前面。
  const renderOrder = useMemo(() => {
    const order = routes.map((_, idx) => idx)
    const rank = (idx: number) =>
      blueHighlightedIndices?.has(idx) ? 2 : isHighlightedIndex(idx) ? 1 : 0
    return order.sort((a, b) => {
      const ra = rank(a)
      const rb = rank(b)
      if (ra !== rb) return ra - rb
      return a - b
    })
  }, [routes, isHighlightedIndex, blueHighlightedIndices])

  const reorderedRoutes = useMemo(
    () => renderOrder.map((idx) => routes[idx]),
    [renderOrder, routes]
  )

  // ハイライトが有効なとき、非選択ルートは薄い色にして選択を目立たせる
  const hasHighlight =
    (highlightedIndices && highlightedIndices.size > 0) ||
    (blueHighlightedIndices && blueHighlightedIndices.size > 0) ||
    (selectedRouteIndex !== null && selectedRouteIndex !== undefined)

  const getRouteColor = useMemo(() => {
    return (routeIndex: number) => {
      if (dimRoutes) return '#bfdbfe' // ルート編集モードの背景ルートは淡い青
      const actualRouteIndex = renderOrder[routeIndex]
      if (blueHighlightedIndices?.has(actualRouteIndex)) {
        return '#2563eb' // 使用ルート一覧で選択したルートは青
      }
      if (highlightLastRoute && actualRouteIndex === routes.length - 1) {
        return '#10b981' // プレビュールート（最後）は緑
      }
      if (isHighlightedIndex(actualRouteIndex)) {
        return '#f97316' // ハイライトはオレンジ
      }
      return hasHighlight ? '#bfdbfe' : '#3b82f6' // 選択時の非対象は淡い青
    }
  }, [
    dimRoutes,
    renderOrder,
    highlightLastRoute,
    routes.length,
    isHighlightedIndex,
    blueHighlightedIndices,
    hasHighlight,
  ])

  const isRouteHighlighted = useMemo(() => {
    return (routeIndex: number) => {
      const actualRouteIndex = renderOrder[routeIndex]
      return (
        (highlightLastRoute && actualRouteIndex === routes.length - 1) ||
        isHighlightedIndex(actualRouteIndex)
      )
    }
  }, [renderOrder, highlightLastRoute, routes.length, isHighlightedIndex])

  const getDisplayIndex = useMemo(() => {
    return (routeIndex: number) => renderOrder[routeIndex]
  }, [renderOrder])

  const routesToRender = reorderedRoutes

  // クリックで選択可能な点（スロット + 描画中ルートの中継点）。
  // スロットは同一位置に重なっていても全件残す（ルート共通の接続スロットを
  // まとめて選択・一括編集できるようにする）。中継点はスロットと重ならない
  // 位置のみ、位置で重複排除して追加する。
  const selectablePoints = useMemo<SelectedPoint[]>(() => {
    if (!map) return []
    const posKey = (x: number, y: number) => `${x.toFixed(3)},${y.toFixed(3)}`
    const points: SelectedPoint[] = []
    const slotPositions = new Set<string>()

    for (const slot of map.slots.values()) {
      const st = map.stations.get(slot.stationId)
      if (!st?.pose) continue
      const isCargo = ENABLE_CARGO_TOGGLE && slot.type === SLOT_TYPE_CARGO
      points.push({
        id: slot.id,
        kind: 'slot',
        x: st.pose.x,
        y: st.pose.y,
        theta: st.pose.theta ?? 0,
        isCargo,
        height: isCargo ? slot.height : undefined,
      })
      slotPositions.add(posKey(st.pose.x, st.pose.y))
    }

    // 描画中ルートの中継点
    const seenWaypoints = new Set<string>()
    for (const route of routesToRender) {
      const startId = route.startSlot || route.slots[0]
      const endId = route.endSlot || route.slots[1] || route.slots[0]
      const stations = buildRoutePoints(map, startId, endId)
      stations?.forEach((st) => {
        if (!st.pose) return
        const key = posKey(st.pose.x, st.pose.y)
        if (slotPositions.has(key) || seenWaypoints.has(key)) return
        seenWaypoints.add(key)
        points.push({
          id: st.id,
          kind: 'point',
          x: st.pose.x,
          y: st.pose.y,
          theta: st.pose.theta ?? 0,
        })
      })
    }

    return points
  }, [map, routesToRender])

  // 線ヒットテスト用: 描画中ルートごとのワールド座標パス
  const routeWorldPaths = useMemo(() => {
    if (!map) return []
    return routesToRender.map((route) => {
      const startId = route.startSlot || route.slots[0]
      const endId = route.endSlot || route.slots[1] || route.slots[0]
      const stations = buildRoutePoints(map, startId, endId) ?? []
      const pts = stations
        .filter((st) => st.pose)
        .map((st) => ({ x: st.pose!.x, y: st.pose!.y }))
      return { route, pts }
    })
  }, [map, routesToRender])

  const centerPose = useMemo<Pose | null>(() => {
    if (!activeBounds) return null
    return { x: activeBounds.centerX, y: activeBounds.centerY, theta: 0 }
  }, [activeBounds])

  // 現在のビュー状態でワールド→キャンバス変換関数を生成する
  const getWorldToCanvas = (canvas: HTMLCanvasElement) => {
    const baseWidthMeter =
      effectiveCanvasWidthMeter ?? canvasParams.canvasWidthMeter
    const center = centerPose ?? { x: 0, y: 0 }
    return createWorldToCanvas(
      canvas,
      { x: center.x, y: center.y },
      offset,
      scale,
      baseWidthMeter
    )
  }

  // クライアント座標をワールド座標 (m) に変換する
  const clientToWorld = (
    canvas: HTMLCanvasElement,
    clientX: number,
    clientY: number
  ) => {
    const rect = canvas.getBoundingClientRect()
    const canvasX = ((clientX - rect.left) / rect.width) * canvas.width
    const canvasY = ((clientY - rect.top) / rect.height) * canvas.height
    const baseWidthMeter =
      effectiveCanvasWidthMeter ?? canvasParams.canvasWidthMeter
    const effectiveWidthMeter = baseWidthMeter / scale
    const canvasHeightMeter =
      (canvas.height / canvas.width) * effectiveWidthMeter
    const center = centerPose ?? { x: 0, y: 0 }
    return {
      x:
        ((canvasX - canvas.width / 2) * effectiveWidthMeter) / canvas.width +
        center.x +
        offset.x,
      y:
        -(((canvasY - canvas.height / 2) * canvasHeightMeter) / canvas.height) +
        center.y -
        offset.y,
    }
  }

  // ワールド座標 → クライアント座標（マークの掴みオフセット計算用）
  const worldToClient = (canvas: HTMLCanvasElement, x: number, y: number) => {
    const rect = canvas.getBoundingClientRect()
    const p = getWorldToCanvas(canvas)(x, y)
    return {
      x: rect.left + (p.x / canvas.width) * rect.width,
      y: rect.top + (p.y / canvas.height) * rect.height,
    }
  }

  // 編集マークのグリフ描画サイズ（スロットと同じ実寸スケール。下限あり）
  const getMarkSize = (canvas: HTMLCanvasElement) => {
    const baseWidthMeter =
      effectiveCanvasWidthMeter ?? canvasParams.canvasWidthMeter
    const pixelsPerMeter = canvas.width / (baseWidthMeter / scale)
    return Math.max(pixelsPerMeter * 0.9, 30)
  }

  // 編集マークのヒットテスト（クライアント座標 → マークインデックス）
  const hitTestEditMark = (
    canvas: HTMLCanvasElement,
    clientX: number,
    clientY: number
  ): number | null => {
    if (!editMarks || editMarks.length === 0) return null
    const rect = canvas.getBoundingClientRect()
    const dpr = canvas.width / rect.width
    const cx = (clientX - rect.left) * dpr
    const cy = (clientY - rect.top) * dpr
    const worldToCanvas = getWorldToCanvas(canvas)
    // グリフ（フォークリフト型）が大きく描かれているときは判定半径も追従させる
    const hitRadius = Math.max(
      EDIT_MARK_HIT_RADIUS * dpr,
      getMarkSize(canvas) * 0.7
    )
    let best: number | null = null
    let bestDist = hitRadius ** 2
    editMarks.forEach((m, i) => {
      const p = worldToCanvas(m.x, m.y)
      const d = (p.x - cx) ** 2 + (p.y - cy) ** 2
      if (d <= bestDist) {
        bestDist = d
        best = i
      }
    })
    return best
  }

  // マーク追加 / ドラッグ時のスロット吸着: クライアント座標の近傍にある
  // 既存スロットを返す。スロットグリフは実寸スケール（pixelsPerMeter）で
  // 描画されるため、判定半径もグリフサイズに追従させる（下限 22px）。
  // 吸着したマークはスロットと座標が一致し、シナリオの接続判定
  // （L1 距離 <= DIST_THRESHOLD）を確実に通せる。
  const findSnapSlot = (
    canvas: HTMLCanvasElement,
    clientX: number,
    clientY: number
  ): SelectedPoint | null => {
    const rect = canvas.getBoundingClientRect()
    const dpr = canvas.width / rect.width
    const cx = (clientX - rect.left) * dpr
    const cy = (clientY - rect.top) * dpr
    const worldToCanvas = getWorldToCanvas(canvas)
    const baseWidthMeter =
      effectiveCanvasWidthMeter ?? canvasParams.canvasWidthMeter
    const pixelsPerMeter = canvas.width / (baseWidthMeter / scale)
    const hitRadius = Math.max(22 * dpr, pixelsPerMeter * 0.7)
    let best: SelectedPoint | null = null
    let bestDist = hitRadius ** 2
    for (const p of selectablePoints) {
      if (p.kind !== 'slot') continue
      const c = worldToCanvas(p.x, p.y)
      const d = (c.x - cx) ** 2 + (c.y - cy) ** 2
      if (d <= bestDist) {
        bestDist = d
        best = p
      }
    }
    return best
  }

  // 回転ハンドル（選択中の端点マークの theta 方向の先）のヒットテスト
  const hitTestRotateHandle = (
    canvas: HTMLCanvasElement,
    clientX: number,
    clientY: number
  ): boolean => {
    if (rotatableMarkIndex == null || !editMarks) return false
    const mark = editMarks[rotatableMarkIndex]
    if (!mark) return false
    const rect = canvas.getBoundingClientRect()
    const dpr = canvas.width / rect.width
    const cx = (clientX - rect.left) * dpr
    const cy = (clientY - rect.top) * dpr
    const worldToCanvas = getWorldToCanvas(canvas)
    const p = worldToCanvas(mark.x, mark.y)
    const size = getMarkSize(canvas)
    const { dist, radius } = editRotateHandleGeometry(size)
    const hx = p.x + Math.cos(-mark.theta) * dist
    const hy = p.y + Math.sin(-mark.theta) * dist
    const hitRadius = Math.max(radius + 6 * dpr, 14 * dpr)
    return (hx - cx) ** 2 + (hy - cy) ** 2 <= hitRadius ** 2
  }

  // autoFit 時、フィット対象（activeBounds）が変わったときのみビューをリセットする。
  // 描画対象 routes だけ・選択だけが変わってもリセットしない（縮尺を保つ）。
  useEffect(() => {
    if (autoFit) handleResetView()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBounds, autoFit])

  // キャンバス描画
  useEffect(() => {
    if (!canvasRef.current) return
    const canvas = canvasRef.current
    const baseCanvasWidthMeter =
      effectiveCanvasWidthMeter ?? canvasParams.canvasWidthMeter
    const center = centerPose ?? { x: 0, y: 0, theta: 0 }

    const worldToCanvas = createWorldToCanvas(
      canvas,
      { x: center.x, y: center.y },
      offset,
      scale,
      baseCanvasWidthMeter
    )

    drawRouteMapCanvas({
      canvas,
      pose: center,
      map,
      routesToRender,
      bgImage,
      imageLoaded,
      scale,
      offset,
      canvasParams,
      baseCanvasWidthMeter,
      showSlots,
      showSlotLabels,
      worldToCanvas,
      getRouteColor,
      isRouteHighlighted,
      showRouteActionLabel: () => true,
      getDisplayIndex,
      showIntermediatePoints: true,
      showRouteSlots: true,
      showArrow: true,
      slotStyle: 'default',
      showGrid: true,
      animateForkDirection: hasForkAnimation,
      forkAnimRoutes,
      forkAnimStartMs: forkAnimStartRef.current,
      forkAnimFirstRouteIndex,
      drawBackgroundImage,
      drawForklift,
    })

    // ルート編集モード: 編集中のマーク列を最前面に描画する
    if (editMarks) {
      const editCtx = canvas.getContext('2d')
      if (editCtx) {
        drawEditMarks(
          editCtx,
          worldToCanvas,
          editMarks,
          selectedMarkIndex,
          markErrorIndices ?? new Set(),
          getMarkSize(canvas),
          drawForklift,
          rotatableMarkIndex
        )
      }
    }

    // 選択中の点のハイライト（黄色いリング + 向きの矢印）。複数選択にも対応。
    // 平行移動プレビュー中は移動対象を移動先に描き、元位置を破線で示す。
    if (selectedPointIds && selectedPointIds.size > 0) {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        const off = selectionOffset ?? { x: 0, y: 0 }
        const hasOffset = off.x !== 0 || off.y !== 0
        for (const selected of selectablePoints) {
          if (!selectedPointIds.has(selected.id)) continue
          const moving =
            hasOffset && (movablePointIds?.has(selected.id) ?? false)
          const pos = moving
            ? worldToCanvas(selected.x + off.x, selected.y + off.y)
            : worldToCanvas(selected.x, selected.y)
          const r = POINT_RING_R
          if (moving) {
            // 元位置の破線の円と、移動先まで結ぶ破線
            const orig = worldToCanvas(selected.x, selected.y)
            ctx.setLineDash([4, 4])
            ctx.strokeStyle = 'rgba(107,114,128,0.8)'
            ctx.lineWidth = 2
            ctx.beginPath()
            ctx.arc(orig.x, orig.y, r, 0, Math.PI * 2)
            ctx.stroke()
            ctx.beginPath()
            ctx.moveTo(orig.x, orig.y)
            ctx.lineTo(pos.x, pos.y)
            ctx.stroke()
            ctx.setLineDash([])
          }
          ctx.beginPath()
          ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2)
          ctx.strokeStyle = '#facc15'
          ctx.lineWidth = 4
          ctx.stroke()
          ctx.beginPath()
          ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2)
          ctx.strokeStyle = 'rgba(0,0,0,0.5)'
          ctx.lineWidth = 1.5
          ctx.stroke()
          // 向きの矢印（theta 方向、Canvas は Y 下向きなので -theta）
          const len = r + 16
          const tx = pos.x + Math.cos(-selected.theta) * len
          const ty = pos.y + Math.sin(-selected.theta) * len
          ctx.beginPath()
          ctx.moveTo(pos.x, pos.y)
          ctx.lineTo(tx, ty)
          ctx.strokeStyle = '#facc15'
          ctx.lineWidth = 4
          ctx.stroke()
          const ah = 7
          const ang = Math.atan2(ty - pos.y, tx - pos.x)
          ctx.beginPath()
          ctx.moveTo(tx, ty)
          ctx.lineTo(
            tx - ah * Math.cos(ang - Math.PI / 6),
            ty - ah * Math.sin(ang - Math.PI / 6)
          )
          ctx.moveTo(tx, ty)
          ctx.lineTo(
            tx - ah * Math.cos(ang + Math.PI / 6),
            ty - ah * Math.sin(ang + Math.PI / 6)
          )
          ctx.stroke()
          // 回転ハンドル（端点スロット単独選択時。ドラッグで向きを変更）
          if (rotatablePointId === selected.id && onPointRotate) {
            const hx =
              pos.x + Math.cos(-selected.theta) * POINT_ROTATE_HANDLE_DIST
            const hy =
              pos.y + Math.sin(-selected.theta) * POINT_ROTATE_HANDLE_DIST
            ctx.beginPath()
            ctx.moveTo(tx, ty)
            ctx.lineTo(hx, hy)
            ctx.strokeStyle = 'rgba(250,204,21,0.6)'
            ctx.lineWidth = 2
            ctx.stroke()
            ctx.beginPath()
            ctx.arc(hx, hy, POINT_ROTATE_HANDLE_R, 0, Math.PI * 2)
            ctx.fillStyle = '#fff'
            ctx.fill()
            ctx.strokeStyle = '#f59e0b'
            ctx.lineWidth = 3
            ctx.stroke()
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    centerPose,
    map,
    routesToRender,
    bgImage,
    imageLoaded,
    scale,
    offset,
    canvasParams,
    canvasSize.width,
    canvasSize.height,
    showSlots,
    showSlotLabels,
    effectiveCanvasWidthMeter,
    getRouteColor,
    isRouteHighlighted,
    getDisplayIndex,
    selectedPointIds,
    movablePointIds,
    rotatablePointId,
    selectionOffset,
    selectablePoints,
    hasForkAnimation,
    forkAnimRoutes,
    forkAnimFirstRouteIndex,
    forkAnimTick,
    editMarks,
    selectedMarkIndex,
    markErrorIndices,
    rotatableMarkIndex,
  ])

  // --- 矩形（ラバーバンド）選択 / 編集マークのドラッグ・回転 ---
  const handleRectPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    pointerDownRef.current = { x: e.clientX, y: e.clientY }
    // 編集モード: 回転ハンドル → マーク本体の順にヒットテストし、
    // ドラッグ操作を開始する（panDisabledRef を立ててパンを抑止する）
    if (isEditingMarks && !isAddingMark && canvasRef.current) {
      if (
        onMarkRotate &&
        rotatableMarkIndex != null &&
        hitTestRotateHandle(canvasRef.current, e.clientX, e.clientY)
      ) {
        rotatingMarkRef.current = rotatableMarkIndex
        panDisabledRef.current = true
        canvasRef.current.setPointerCapture(e.pointerId)
        onMarkRotateStart?.(rotatableMarkIndex)
        return
      }
      if (onMarkDrag) {
        const idx = hitTestEditMark(canvasRef.current, e.clientX, e.clientY)
        if (idx !== null) {
          draggingMarkRef.current = idx
          const mark = editMarks?.[idx]
          const grab = mark
            ? worldToClient(canvasRef.current, mark.x, mark.y)
            : { x: e.clientX, y: e.clientY }
          markDragRef.current = {
            startClientX: e.clientX,
            startClientY: e.clientY,
            grabDx: grab.x - e.clientX,
            grabDy: grab.y - e.clientY,
            active: false,
          }
          panDisabledRef.current = true
          canvasRef.current.setPointerCapture(e.pointerId)
          onSelectMark?.(idx)
          onMarkDragStart?.(idx)
          return
        }
      }
    }
    // 参照モード: 選択点の回転ハンドル上でポインタを押したら回転ドラッグを開始
    if (
      !selectionMode &&
      !isEditingMarks &&
      onPointRotate &&
      rotatablePointId &&
      canvasRef.current
    ) {
      const canvas = canvasRef.current
      const target = selectablePoints.find((q) => q.id === rotatablePointId)
      if (target) {
        const rect = canvas.getBoundingClientRect()
        const dpr = canvas.width / rect.width
        const cx = (e.clientX - rect.left) * dpr
        const cy = (e.clientY - rect.top) * dpr
        const worldToCanvas = getWorldToCanvas(canvas)
        const p = worldToCanvas(target.x, target.y)
        const hx = p.x + Math.cos(-target.theta) * POINT_ROTATE_HANDLE_DIST
        const hy = p.y + Math.sin(-target.theta) * POINT_ROTATE_HANDLE_DIST
        const hitR = Math.max(POINT_ROTATE_HANDLE_R + 8, 20)
        if ((hx - cx) ** 2 + (hy - cy) ** 2 <= hitR ** 2) {
          rotatingPointRef.current = { x: target.x, y: target.y }
          rotatedPointerRef.current = true
          panDisabledRef.current = true
          canvas.setPointerCapture(e.pointerId)
          onPointRotateStart?.()
          return
        }
      }
    }
    // 参照モード: 選択中スロットの上でポインタを押したら選択全体のドラッグ移動を開始
    if (
      !selectionMode &&
      !isEditingMarks &&
      onSelectionDrag &&
      movablePointIds &&
      movablePointIds.size > 0 &&
      canvasRef.current
    ) {
      const canvas = canvasRef.current
      const rect = canvas.getBoundingClientRect()
      const dpr = canvas.width / rect.width
      const cx = (e.clientX - rect.left) * dpr
      const cy = (e.clientY - rect.top) * dpr
      const worldToCanvas = getWorldToCanvas(canvas)
      const off = selectionOffset ?? { x: 0, y: 0 }
      const hitRadiusSq = (22 * dpr) ** 2
      const hit = selectablePoints.some((p) => {
        if (!movablePointIds.has(p.id)) return false
        // 未保存の移動オフセットを反映した表示位置でヒットテストする
        const c = worldToCanvas(p.x + off.x, p.y + off.y)
        return (c.x - cx) ** 2 + (c.y - cy) ** 2 <= hitRadiusSq
      })
      if (hit) {
        selectionDragRef.current = {
          startClientX: e.clientX,
          startClientY: e.clientY,
          startWorld: clientToWorld(canvas, e.clientX, e.clientY),
          active: false,
        }
        panDisabledRef.current = true
        canvas.setPointerCapture(e.pointerId)
        return
      }
    }
    if (!selectionMode || !onSelectSlots || !canvasRef.current) return
    const rect = canvasRef.current.getBoundingClientRect()
    const start = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    rectStartRef.current = start
    selectingRef.current = true
    canvasRef.current.setPointerCapture(e.pointerId)
    setDragRect({ left: start.x, top: start.y, width: 0, height: 0 })
  }

  const handleRectPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // 参照モード: 選択点の回転ハンドルのドラッグ（中心からの方位角を通知）
    if (rotatingPointRef.current && canvasRef.current) {
      const center = rotatingPointRef.current
      const world = clientToWorld(canvasRef.current, e.clientX, e.clientY)
      onPointRotate?.(Math.atan2(world.y - center.y, world.x - center.x))
      return
    }
    if (rotatingMarkRef.current !== null && canvasRef.current && editMarks) {
      const mark = editMarks[rotatingMarkRef.current]
      if (mark) {
        // マーク中心からポインタ位置への方位角を新しい向きにする
        const world = clientToWorld(canvasRef.current, e.clientX, e.clientY)
        const theta = Math.atan2(world.y - mark.y, world.x - mark.x)
        onMarkRotate?.(rotatingMarkRef.current, theta)
      }
      return
    }
    if (draggingMarkRef.current !== null && canvasRef.current) {
      const drag = markDragRef.current
      if (drag && !drag.active) {
        const dx = e.clientX - drag.startClientX
        const dy = e.clientY - drag.startClientY
        // 微小移動はクリック扱いのままにする（6px 超でドラッグ確定）。
        // これが無いとクリック時の手ぶれでマーク中心がポインタへ飛ぶ。
        if (dx * dx + dy * dy <= 36) return
        drag.active = true
      }
      // 掴んだ位置のずれを保ったマーク中心の位置で吸着・移動を判定する
      const cx = e.clientX + (drag?.grabDx ?? 0)
      const cy = e.clientY + (drag?.grabDy ?? 0)
      // 既存スロットの近くへドラッグしたらスロット位置へ吸着させる
      const snap = findSnapSlot(canvasRef.current, cx, cy)
      onMarkDrag?.(
        draggingMarkRef.current,
        snap
          ? { x: snap.x, y: snap.y }
          : clientToWorld(canvasRef.current, cx, cy),
        snap ? snap.theta : undefined
      )
      return
    }
    if (selectionDragRef.current && canvasRef.current) {
      const drag = selectionDragRef.current
      if (!drag.active) {
        const dx = e.clientX - drag.startClientX
        const dy = e.clientY - drag.startClientY
        // 微小移動はクリック扱いのままにする（6px 超でドラッグ確定）
        if (dx * dx + dy * dy <= 36) return
        drag.active = true
        onSelectionDragStart?.()
      }
      const world = clientToWorld(canvasRef.current, e.clientX, e.clientY)
      onSelectionDrag?.({
        x: world.x - drag.startWorld.x,
        y: world.y - drag.startWorld.y,
      })
      return
    }
    if (!selectingRef.current || !rectStartRef.current || !canvasRef.current)
      return
    const rect = canvasRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left
    const cy = e.clientY - rect.top
    const start = rectStartRef.current
    setDragRect({
      left: Math.min(cx, start.x),
      top: Math.min(cy, start.y),
      width: Math.abs(cx - start.x),
      height: Math.abs(cy - start.y),
    })
  }

  const handleRectPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (rotatingPointRef.current) {
      rotatingPointRef.current = null
      panDisabledRef.current = selectionMode
      onPointRotateEnd?.()
      return
    }
    if (rotatingMarkRef.current !== null) {
      rotatingMarkRef.current = null
      panDisabledRef.current = selectionMode
      onMarkRotateEnd?.()
      return
    }
    if (draggingMarkRef.current !== null) {
      draggingMarkRef.current = null
      markDragRef.current = null
      panDisabledRef.current = selectionMode
      onMarkDragEnd?.()
      return
    }
    if (selectionDragRef.current) {
      const wasActive = selectionDragRef.current.active
      selectionDragRef.current = null
      panDisabledRef.current = selectionMode
      // ドラッグ未確定（微小移動）の場合は通常のクリックに任せる
      if (wasActive) onSelectionDragEnd?.()
      return
    }
    if (!selectingRef.current || !rectStartRef.current || !canvasRef.current)
      return
    selectingRef.current = false
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const start = rectStartRef.current
    rectStartRef.current = null
    setDragRect(null)
    const endX = e.clientX - rect.left
    const endY = e.clientY - rect.top
    const dpr = canvas.width / rect.width
    const minX = Math.min(start.x, endX) * dpr
    const maxX = Math.max(start.x, endX) * dpr
    const minY = Math.min(start.y, endY) * dpr
    const maxY = Math.max(start.y, endY) * dpr
    // 微小な矩形（実質クリック）は無視して onClick に任せる
    if (maxX - minX < 5 * dpr && maxY - minY < 5 * dpr) return

    const baseWidthMeter =
      effectiveCanvasWidthMeter ?? canvasParams.canvasWidthMeter
    const center = centerPose ?? { x: 0, y: 0 }
    const worldToCanvas = createWorldToCanvas(
      canvas,
      { x: center.x, y: center.y },
      offset,
      scale,
      baseWidthMeter
    )
    // 矩形内の点（スロット / 中継点）を選択する（ルートは対象外）
    const hits: SelectedPoint[] = []
    for (const p of selectablePoints) {
      const c = worldToCanvas(p.x, p.y)
      if (c.x >= minX && c.x <= maxX && c.y >= minY && c.y <= maxY) {
        hits.push(p)
      }
    }
    onSelectSlots?.(hits, {
      ctrl: e.ctrlKey || e.metaKey,
      shift: e.shiftKey,
    })
    // スロットを選択できたら矩形選択モードを解除し、
    // そのまま選択スロットのドラッグ移動へ移れるようにする
    if (hits.length > 0) setSelectionMode(false)
  }

  return (
    <div ref={containerRef} className={className}>
      <div
        ref={mapContainerRef}
        className={`relative w-full h-full select-none ${preview ? 'pointer-events-none' : ''}`}
      >
        {/* 左: ツール（フォトショップ風にコントロール系は左） */}
        {showControls && !preview && (
          <div className="absolute top-2 left-2 z-10 flex flex-col gap-2">
            {onSelectSlots && (
              <Button
                onClick={() => setSelectionMode((v) => !v)}
                variant={selectionMode ? 'blue' : 'white'}
                className="p-2 shadow"
                title={t('scenarios.selectMode')}
              >
                <BoxSelect size={18} />
              </Button>
            )}
            {/* 爪方向アニメーションはルート編集中は無効のためボタンも出さない */}
            {animSourceRoutes.length > 0 && !isEditingMarks && (
              <Button
                onClick={() => setForkAnimEnabled((v) => !v)}
                variant={forkAnimEnabled ? 'blue' : 'white'}
                className="p-2 shadow"
                title={t('scenarios.forkAnimation')}
              >
                {forkAnimEnabled ? <Pause size={18} /> : <Play size={18} />}
              </Button>
            )}
          </div>
        )}

        {/* 右上: マップ縮尺（ズーム / フィット）のみ */}
        {showControls && !preview && (
          <MapCanvasControls
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onFitImage={imageBounds ? handleFitImage : undefined}
            onFitSelected={selectedBounds ? handleFitSelected : undefined}
          />
        )}

        {/* 矩形選択のプレビュー */}
        {dragRect && (
          <div
            className="absolute z-10 border-2 border-blue-500 bg-blue-500/10 pointer-events-none"
            style={{
              left: dragRect.left,
              top: dragRect.top,
              width: dragRect.width,
              height: dragRect.height,
            }}
          />
        )}

        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          className={`${canvasClassName} ${preview ? 'pointer-events-none' : selectionMode || isAddingMark ? 'cursor-crosshair' : 'cursor-pointer'}`}
          style={{ touchAction: 'none' }}
          onPointerDown={handleRectPointerDown}
          onPointerMove={handleRectPointerMove}
          onPointerUp={handleRectPointerUp}
          onClick={(e) => {
            if (!canvasRef.current) return
            // 回転ハンドル操作後のクリックは無視する（選択解除を防ぐ）
            if (rotatedPointerRef.current) {
              rotatedPointerRef.current = false
              pointerDownRef.current = null
              return
            }
            // ドラッグ（パン）後のクリックは無視する（5px 超の移動はドラッグ扱い）
            const down = pointerDownRef.current
            pointerDownRef.current = null
            if (down) {
              const dx = e.clientX - down.x
              const dy = e.clientY - down.y
              if (dx * dx + dy * dy > 25) return
            }

            const canvas = canvasRef.current
            const rect = canvas.getBoundingClientRect()
            const canvasX =
              ((e.clientX - rect.left) / rect.width) * canvas.width
            const canvasY =
              ((e.clientY - rect.top) / rect.height) * canvas.height
            const baseWidthMeter =
              effectiveCanvasWidthMeter ?? canvasParams.canvasWidthMeter
            const effectiveWidthMeter = baseWidthMeter / scale
            const canvasHeightMeter =
              (canvas.height / canvas.width) * effectiveWidthMeter
            const center = centerPose ?? { x: 0, y: 0 }
            const worldX =
              ((canvasX - canvas.width / 2) * effectiveWidthMeter) /
                canvas.width +
              center.x +
              offset.x
            const worldY =
              -(
                ((canvasY - canvas.height / 2) * canvasHeightMeter) /
                canvas.height
              ) +
              center.y -
              offset.y

            // 編集モード: マーク追加 / マーク選択のみを行う
            if (isEditingMarks) {
              if (isAddingMark) {
                // 既存スロットの近くなら吸着情報を渡す（座標・荷役高さの引き継ぎ）
                const snap = findSnapSlot(canvas, e.clientX, e.clientY)
                onAddMarkAt?.({ x: worldX, y: worldY }, snap ?? undefined)
                return
              }
              onSelectMark?.(hitTestEditMark(canvas, e.clientX, e.clientY))
              return
            }

            const worldToCanvas = createWorldToCanvas(
              canvas,
              { x: center.x, y: center.y },
              offset,
              scale,
              baseWidthMeter
            )
            const dpr = canvas.width / rect.width
            const click = { x: canvasX, y: canvasY }

            // まず点（スロット / 中継点）を最近傍でヒットテスト
            const pointThreshold = 22 * dpr
            let nearestPoint: SelectedPoint | null = null
            let nearestPointDist = pointThreshold * pointThreshold
            for (const p of selectablePoints) {
              const c = worldToCanvas(p.x, p.y)
              const d = (c.x - click.x) ** 2 + (c.y - click.y) ** 2
              if (d <= nearestPointDist) {
                nearestPointDist = d
                nearestPoint = p
              }
            }

            const mods = {
              ctrl: e.ctrlKey || e.metaKey,
              shift: e.shiftKey,
            }

            // 修飾キー付きクリック（複数選択が有効な場合のみ）は
            // スロット / 中継点を対象にし、ルートの選択状態は変えない。
            // 同一位置に重なっている点（ルート共通の接続スロット）は
            // まとめて選択対象にする。
            if ((mods.ctrl || mods.shift) && onSelectSlots) {
              if (nearestPoint) {
                const target = nearestPoint
                const group = selectablePoints.filter(
                  (q) =>
                    Math.abs(q.x - target.x) < 1e-3 &&
                    Math.abs(q.y - target.y) < 1e-3
                )
                if (group.length > 1) {
                  onSelectSlots(group, mods)
                } else {
                  onSelectPoint?.(nearestPoint, mods)
                }
              }
            } else if (nearestPoint) {
              onSelectPoint?.(nearestPoint, mods)
              // 点が属するルート（mapId 一致）を選択
              const mapId = nearestPoint.id.match(/^\*r([^/]+)\//)?.[1]
              const route =
                (mapId && routes.find((r) => r.mapId === mapId)) || null
              onSelectRoute?.(route, mods)
            } else {
              // 点に当たらなければ、ルートの線をヒットテスト
              const lineThreshold = 10 * dpr
              let nearestRoute: RouteWithAction | null = null
              let nearestLineDist = lineThreshold * lineThreshold
              for (const { route, pts } of routeWorldPaths) {
                if (pts.length < 2) continue
                const cpts = pts.map((p) => worldToCanvas(p.x, p.y))
                for (let i = 1; i < cpts.length; i++) {
                  const d = distToSegmentSq(click, cpts[i - 1], cpts[i])
                  if (d <= nearestLineDist) {
                    nearestLineDist = d
                    nearestRoute = route
                  }
                }
              }
              onSelectPoint?.(null, mods)
              onSelectRoute?.(nearestRoute, mods)
            }

            onMapClick?.({ x: worldX, y: worldY })
          }}
        />
      </div>
    </div>
  )
}
